import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  PLATFORM_ID,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {BaseIconConfig, IconConfig, MapStep, ZoomSpec} from '../../../shared/map/map.types';
import {MapService} from '../../../shared/map/map.service';
import {MapMarkerSize} from '../../../shared/map/map.constants';
import warsawBoundaries from "../../../assets/parking/geodata/warsaw-boundaries.json";
import {isPlatformBrowser} from '@angular/common';
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from 'primeng/accordion';
import {Button} from 'primeng/button';

@Component({
  selector: 'app-parking-location',
  imports: [
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    Accordion,
    Button
  ],
  templateUrl: './parking-location.component.html',
  styleUrl: './parking-location.component.scss'
})
export class ParkingLocationComponent implements OnDestroy {
  @ViewChild("autoParkMap", {static: false}) autoparkMap!: ElementRef;
  @ViewChild("warsawBoundariesMap", {static: false}) warsawBoundariesMap!: ElementRef;
  @ViewChild("metroMap", {static: false}) metroMap!: ElementRef;
  @ViewChild("highWayMap", {static: false}) highWayMap!: ElementRef;

  private readonly parkingLat: number = 52.217165;
  private readonly parkingLng: number = 20.888829;
  protected mapSteps: WritableSignal<MapStep[]> = signal<MapStep[]>([]);
  private resizeObserver?: ResizeObserver;

  private readonly platformId = inject(PLATFORM_ID);
  protected readonly isBrowser = isPlatformBrowser(this.platformId)


  constructor(
    private readonly mapService: MapService
  ) {
    afterNextRender(() => {
      this.mapSteps.set([
        {
          mapElement: this.autoparkMap,
          coords: [this.parkingLat, this.parkingLng],
          zoomControl: false,
          zoom: 16,
          label: $localize`:@@autopark.location.tab1.label:Lokalizacja`,
          title: $localize`:@@autopark.location.tab1.title:Plac Depozytowy, ul. Połczyńska 110`,
          content: $localize`:@@autopark.location.tab1.content:Centralny punkt operacyjny ProcessWay z pełną infrastrukturą logistyczną dla pojazdów i sprzętu flotowego`,
          iconable: true,
          iconPath: "assets/parking/images/map-pin-parking.png",
          iconSize: MapMarkerSize.MEDIUM
        },
        {
          mapElement: this.warsawBoundariesMap,
          coords: [52.232531, 21.006747],
          zoomControl: false,
          zoom: 14.5,
          label: $localize`:@@autopark.location.tab2.label:Teren`,
          title: $localize`:@@autopark.location.tab2.title:Obrzeża Warszawy`,
          iconable: false,
          content: $localize`:@@autopark.location.tab2.content:strategiczna lokalizacja z dogodnym dojazdem dla flot wjeżdżających i wyjeżdżających z miasta, idealna na plac przeładunkowo-depozytowy`,
          iconPath: "assets/parking/images/map-pin-waw.png",
          iconSize: MapMarkerSize.MEDIUM,
          geojsonFilePath: "src/assets/parking/geodata/warsaw-boundaries.json"
        },
        {
          mapElement: this.metroMap,
          coords: [52.217309, 20.885766],
          zoomControl: false,
          zoom: 16,
          label: $localize`:@@autopark.location.tab3.label:Otoczenie`,
          title: $localize`:@@autopark.location.tab3.title:Powstająca linia metra`,
          iconable: true,
          content: $localize`:@@autopark.location.tab3.content:dynamiczne otoczenie inwestycyjne generujące stałe zapotrzebowanie na wsparcie flotowe i operacje terenowe`,
          iconPath: "assets/parking/images/map-pin-subway.png",
          iconSize: MapMarkerSize.MEDIUM
        },
        {
          mapElement: this.highWayMap,
          coords: [52.216113, 20.863519],
          zoomControl: false,
          zoom: 17,
          label: $localize`:@@autopark.location.tab4.label:Komunikacja`,
          title: $localize`:@@autopark.location.tab4.title:Wezeł komunikacyjny`,
          iconable: false,
          content: $localize`:@@autopark.location.tab4.content:pobliski węzeł komunikacyjny S8 i A2, sprzyjający planowaniu tranzytu i logistyki`,
        },
      ]);

      this.initMaps().then(() => {
        this.resizeObserver = new ResizeObserver(() => this.onMapResize());
        this.resizeObserver.observe(this.autoparkMap.nativeElement);
        this.resizeObserver.observe(this.warsawBoundariesMap.nativeElement);
        this.resizeObserver.observe(this.metroMap.nativeElement);
        this.resizeObserver.observe(this.highWayMap.nativeElement);

        this.onMapResize();
      });
    });
  }

  public ngOnDestroy(): void {
    this.mapService.destroyAll();
  }

  protected activeIndex: string = "0";

  private async initMaps(): Promise<void> {
    for (const step of this.mapSteps()) {
      const index = this.mapSteps().indexOf(step);
      const mapId = `map-${index}`;
      const zoomSpec: ZoomSpec = {
        zoomLevel: step.zoom,
        zoomControl: step.zoomControl,
      };

      await this.mapService.init(mapId, step.mapElement, step.coords, zoomSpec, false, false);

      if (step.iconable && step.iconPath && step.iconSize) {
        const baseConfig: BaseIconConfig = {
          iconUrl: step.iconPath,
          iconSize: step.iconSize,
        }

        const iconConfig: IconConfig = {
          base: baseConfig,
          coordinates: step.coords
        }

        await this.mapService.addMarker(mapId, iconConfig);
      }

      const map = this.mapService.getMap(mapId);

      this.invalidateSize(map);

      const style: L.PathOptions = {
        color: "#F39200FF",
        weight: 3,
        opacity: 1,
        fillOpacity: 0.2,
      }

      if (step.geojsonFilePath) {
        await this.mapService.addGeoJsonBoundaries(mapId, warsawBoundaries as any, style);
      }
    }
  }

  invalidateSize(map: any) {
    map.invalidateSize();
  }

  onMapResize() {
    const maps = this.mapService.getMaps();

    if (maps) {
      maps.forEach(map => {
        setTimeout(() => map.invalidateSize(), 300);
      })
    }
  }
}
